import { TextareaAutosize } from '@mui/material';
import Button from 'components/Form/Button';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { POST } from 'services/api';
import { ReadOne } from 'services/financing-proposals';
import styled, { keyframes } from 'styled-components';
import { Title } from 'ui/styled';
import { optionsPrazo } from 'utils/options';
import { parseCurrency, unparseCurrency } from 'utils/parsers';
import FormCore from '../../Form/Core';
import { AddProposalContainer, CompanyButtonContainer } from './styled';

const MIN_LENGTH = 50;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
`;

const CuradoriaModalContainer = styled.div`
  padding: 20px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${fadeIn} 0.3s ease-out;
`;

const HeaderInfo = styled.div`
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eeeeee;
`;

const BancoTitle = styled(Title)`
  color: #333333;
  font-size: 1.5em;
  font-weight: 600;
  margin-bottom: 5px;
`;

const ActionButtonContainer = styled(CompanyButtonContainer)`
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
  justify-content: flex-end;

  @media (max-width: 500px) {
    flex-direction: column;
    button {
      width: 100%;
      margin: 5px 0;
    }
  }
`;

const CustomTextarea = styled(TextareaAutosize)`
  width: 100%;
  padding: 10px;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1em;
  font-family: inherit;
  min-height: 100px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    border-color: #007bff;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 0.875em;
  margin-top: 5px;
`;

const ConfirmMessage = styled.div`
  color: #d35400;
  font-weight: 600;
  font-size: 0.95em;
  margin-top: 15px;
  padding: 12px;
  background-color: #fdf5e6;
  border: 1px solid #f5b041;
  border-radius: 6px;
  text-align: center;
`;

// PROPS LIMPAS: removidas url_fatura e url_fatura_da_proposta
export default function ModalCuradoria({ banco, id, reload, onClose }) {
  const [loading, setLoading] = useState(false);
  const [statusCuradoria, setStatusCuradoria] = useState('');
  const [observacao, setObservacao] = useState('');
  const [observacaoError, setObservacaoError] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const formRef = useRef();
  const bancoNome = useMemo(
    () =>
      banco?.banco?.nome_fantasia ||
      banco?.nome_fantasia ||
      banco?.financial ||
      'Banco',
    [banco]
  );

  const close = () => onClose?.();

  const getProposalFormItems = () => [
    {
      ref: 'valor_venda',
      placeholder: 'Valor da venda (R$)',
      half: true,
      type: 'money',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'valor_entrada',
      placeholder: 'Valor Entrada (R$)',
      half: true,
      type: 'money',
      onBlur: () => {
        const entrada = formRef?.current?.getItem('valor_entrada') || 0;
        const venda = formRef?.current?.getItem('valor_venda') || 0;
        const financiado = venda - entrada;
        formRef?.current?.setItem(financiado, 'valor_financiamento');
      },
    },
    {
      ref: 'valor_financiamento',
      placeholder: 'Valor Financiado (R$)',
      half: true,
      type: 'money',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'valor_parcelas',
      placeholder: 'Valor Parcelas (R$)',
      half: true,
      type: 'money',
    },
    {
      ref: 'data',
      placeholder: 'Data da Primeira parcela',
      half: true,
      type: 'date',
    },
    {
      ref: 'parcelas',
      placeholder: 'Prazo (Parcelas)',
      half: true,
      type: 'select',
      options: optionsPrazo,
    },
  ];

  useEffect(() => {
    const fillForm = async () => {
      const form = formRef.current;
      if (!form || !form.setItem || !banco) return;
      try {
        if (banco.valor) {
          const valorFinanciado = unparseCurrency(banco.valor);
          await form.setItem(valorFinanciado, 'valor_venda');
          await form.setItem(valorFinanciado, 'valor_financiamento');
        }
        if (banco.valor_entrada) {
          await form.setItem(
            unparseCurrency(banco.valor_entrada),
            'valor_entrada'
          );
        }
        if (banco.installment_value) {
          await form.setItem(
            unparseCurrency(banco.installment_value),
            'valor_parcelas'
          );
        }
        if (banco.parcelas || banco.deadline) {
          await form.setItem(banco.parcelas || banco.deadline, 'parcelas');
        }
        if (banco.data) {
          const formattedDate = moment(banco.data, [
            'DD/MM/YYYY',
            'YYYY-MM-DD',
          ]).format('YYYY-MM-DD');
          await form.setItem(formattedDate, 'data');
        }
      } catch (err) {
        console.error('Erro ao preencher formulário:', err);
        toast.error('Erro ao preencher os dados da proposta.', {
          autoClose: 5000,
        });
      }
    };
    fillForm();
  }, [banco]);

  useEffect(() => {
    if (banco?.status_curadoria) {
      setStatusCuradoria(banco.status_curadoria.toLowerCase());
    }
    if (banco?.observacao?.trim()) {
      setObservacao(banco.observacao);
    }
  }, [banco]);

  // VALIDAÇÃO FORTE COM BUSCA NO BACKEND
  const handleSaveClick = async () => {
    const form = formRef.current?.getForm();
    let errorMsg = '';
    if (!statusCuradoria) {
      toast.error('É obrigatório selecionar o Status da Curadoria!', {
        autoClose: 5000,
      });
      return;
    }
    if (observacao.trim().length === 0) {
      errorMsg = 'O Campo Observação é obrigatório.';
    } else if (observacao.length < MIN_LENGTH) {
      errorMsg = `A Observação deve ter no mínimo ${MIN_LENGTH} caracteres.`;
    } else if (observacao.length > 500) {
      errorMsg = 'A Observação não pode exceder 500 caracteres.';
    }
    if (errorMsg) {
      setObservacaoError(errorMsg);
      toast.error(errorMsg, { autoClose: 5000 });
      return;
    }
    if (!form?.valor_parcelas || !form?.parcelas) {
      toast.error('Os campos Valor Parcelas e Prazo são obrigatórios.', {
        autoClose: 5000,
      });
      return;
    }
    if (form?.data) {
      const dataParcela = moment(form.data, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);
      if (dataParcela.isBefore(moment().startOf('day'))) {
        toast.error(
          'A data da primeira parcela não pode ser menor que a data atual',
          { autoClose: 5000 }
        );
        return;
      }
    }
    if (form?.valor_parcelas > form?.valor_financiamento) {
      toast.error(
        'O valor da parcela não pode ser maior que o valor financiado',
        { autoClose: 5000 }
      );
      return;
    }
    // VALIDAÇÃO DE APROVAÇÃO COM FATURA OBRIGATÓRIA
    if (statusCuradoria === 'aprovado') {
      if (!form?.data) {
        toast.error(
          'Não é possível aprovar sem informar a Data da Primeira Parcela.',
          { autoClose: 5000 }
        );
        return;
      }
      try {
        const proposta = await ReadOne(id);
        if (!proposta?.url_fatura) {
          toast.error(
            'Fatura de energia obrigatória! Volte à tela anterior e anexe a fatura antes de aprovar.',
            { autoClose: 8000 }
          );
          return;
        }
      } catch (err) {
        toast.error('Erro ao verificar fatura. Tente novamente.', {
          autoClose: 5000,
        });
        console.error('Erro ao buscar proposta:', err);
        return;
      }
    }
    setObservacaoError('');
    setIsConfirming(true);
    toast.warn(
      <div style={{ lineHeight: '1.5', fontWeight: '600' }}>
        Após confirmar, essa ação não poderá ser desfeita.
      </div>,
      { autoClose: 5000, style: { fontSize: '0.95em' } }
    );
  };
  const confirmSave = async () => {
    const form = formRef.current?.getForm();
    setLoading(true);
    const payload = {
      bancoId: banco?.id || banco?.banco?.id,
      propostaId: id,
      novoStatus: statusCuradoria,
      observacao: observacao,
      prazo_financiamento: form?.parcelas,
      data: form?.data || moment().add(1, 'months').format('YYYY-MM-DD'),
      valor_entrada: unparseCurrency(parseCurrency(form?.valor_entrada)),
      valor_parcelas: unparseCurrency(parseCurrency(form?.valor_parcelas)),
      valor_financiamento: unparseCurrency(
        parseCurrency(form?.valor_financiamento)
      ),
    };
    try {
      const result = await POST(
        '/financing-proposal/curadoria/alterastatusbanco',
        payload,
        true
      );
      if (result && result.status === 'ok') {
        const isAprovado = statusCuradoria === 'aprovado';
        const mensagem = isAprovado
          ? `Parabéns! A proposta foi Aprovada no banco ${bancoNome} com sucesso.`
          : `Proposta Recusada. A observação foi registrada.`;
        toast.success(<div style={{ lineHeight: '1.5' }}>{mensagem}</div>, {
          autoClose: 5000,
          style: { fontSize: '0.95em' },
        });
        if (reload) await reload();
        close();
      } else {
        toast.error(`Erro ao salvar: ${result?.erro || 'Tente novamente.'}`, {
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error('Erro na requisição:', error);
      toast.error(`Erro: ${error.message || 'Falha na conexão.'}`, {
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
      setIsConfirming(false);
      // REDIRECIONAMENTO FORÇADO APÓS CONFIRMAR (APROVAR OU RECUSAR)
      window.location.href = '/menu_adm/curadoriapropostas';
    }
  };
  const formItems = useMemo(() => getProposalFormItems(), []);

  return (
    <ModalWrapper onClick={close}>
      <CuradoriaModalContainer onClick={(e) => e.stopPropagation()}>
        <HeaderInfo>
          <BancoTitle small nomargin>
            Curadoria da Proposta
          </BancoTitle>
          <p>
            Banco:
            <span style={{ fontWeight: 'bold', color: '#007bff' }}>
              {bancoNome}
            </span>
          </p>
        </HeaderInfo>

        <AddProposalContainer style={{ marginBottom: '20px' }}>
          <FormCore ref={formRef} formItems={formItems} />
        </AddProposalContainer>

        <div>
          <label htmlFor="statusCuradoria">Status da Curadoria</label>
          <select
            id="statusCuradoria"
            value={statusCuradoria}
            onChange={(e) => setStatusCuradoria(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              marginTop: '5px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '1em',
            }}
          >
            <option value="">Selecione o status</option>
            <option value="recusado">Manter Inelegível</option>
            <option value="aprovado">Aprovar</option>
          </select>
          <p style={{ marginTop: '20px' }}>
            Preencha o campo de <b>Observação</b> para documentação.
          </p>
          <label htmlFor="observacao">Observação</label>
          <CustomTextarea
            id="observacao"
            value={observacao}
            onChange={(e) => {
              const value = e.target.value;
              setObservacao(value);
              if (
                observacaoError &&
                value.length >= MIN_LENGTH &&
                value.length <= 500
              ) {
                setObservacaoError('');
              }
            }}
            placeholder={`Digite sua Observação aqui... (mín. ${MIN_LENGTH} caracteres)`}
            maxLength={500}
          />
          {observacaoError && <ErrorMessage>{observacaoError}</ErrorMessage>}
          {isConfirming && (
            <ConfirmMessage>
              Tem certeza que deseja
              <strong>
                {statusCuradoria === 'aprovado' ? ' APROVAR ' : ' RECUSAR '}
              </strong>
              esta proposta?
            </ConfirmMessage>
          )}
        </div>
        <ActionButtonContainer>
          <Button
            onClick={close}
            color="white"
            border
            nospace
            disabled={loading}
          >
            Fechar
          </Button>
          {isConfirming ? (
            <Button
              color={statusCuradoria === 'aprovado' ? 'black' : 'blue'}
              loading={loading}
              onClick={confirmSave}
              nospace
            >
              Confirmar
              {statusCuradoria === 'aprovado' ? 'Aprovação' : 'Recusa'}
            </Button>
          ) : (
            <Button
              color={
                statusCuradoria === 'aprovado'
                  ? 'black'
                  : statusCuradoria === 'recusado'
                    ? 'blue'
                    : 'blue'
              }
              onClick={handleSaveClick}
              nospace
              disabled={!statusCuradoria || loading}
            >
              {statusCuradoria === 'aprovado'
                ? 'Aprovar Proposta'
                : statusCuradoria === 'recusado'
                  ? 'Manter Inelegível'
                  : 'Salvar Tudo'}
            </Button>
          )}
        </ActionButtonContainer>
      </CuradoriaModalContainer>
    </ModalWrapper>
  );
}
